import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { pricingApi, type PricingAnalytics, type PricingRecommendation } from '../../api/pricing';

export function PricingAnalyticsPage() {
  const [analytics, setAnalytics] = useState<PricingAnalytics | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    void pricingApi.analytics().then(setAnalytics).catch((e) => setError(String(e)));
  }, []);

  return (
    <div className="mx-auto max-w-6xl p-6">
      <Link to="/pricing" className="text-sm text-slate-600 hover:underline">← Pricing hub</Link>
      <h1 className="mt-4 text-2xl font-semibold">Pricing Analytics</h1>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        {analytics?.gross_margin != null && (
          <div className="rounded-xl border bg-white p-4">
            <p className="text-sm text-slate-500">Gross margin</p>
            <p className="mt-1 text-2xl font-semibold">{analytics.gross_margin.toFixed(1)}%</p>
          </div>
        )}
        {Object.entries(analytics?.stats ?? {}).map(([k, v]) => (
          <div key={k} className="rounded-xl border bg-white p-4">
            <p className="text-sm text-slate-500">{k.replace(/_/g, ' ')}</p>
            <p className="mt-1 text-2xl font-semibold">{String(v)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function MarginDashboardPage() {
  const [analytics, setAnalytics] = useState<PricingAnalytics | null>(null);

  useEffect(() => {
    void pricingApi.analytics().then(setAnalytics);
  }, []);

  return (
    <div className="mx-auto max-w-4xl p-6">
      <Link to="/pricing" className="text-sm text-slate-600 hover:underline">← Pricing hub</Link>
      <h1 className="mt-4 text-2xl font-semibold">Margin Dashboard</h1>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div className="rounded-xl border bg-white p-6">
          <p className="text-sm text-slate-500">Gross margin</p>
          <p className="mt-2 text-3xl font-semibold">{analytics?.gross_margin?.toFixed(1) ?? '—'}%</p>
        </div>
        <div className="rounded-xl border bg-white p-6">
          <p className="text-sm text-slate-500">Net margin</p>
          <p className="mt-2 text-3xl font-semibold">{analytics?.net_margin?.toFixed(1) ?? '—'}%</p>
        </div>
      </div>
    </div>
  );
}

export function AIPricingRecommendationsPage() {
  const [productId, setProductId] = useState('');
  const [rec, setRec] = useState<PricingRecommendation | null>(null);
  const [error, setError] = useState('');

  const run = async () => {
    if (!productId.trim()) return;
    try {
      const r = await pricingApi.recommend({ product_id: productId.trim() });
      setRec(r);
    } catch (e) {
      setError(String(e));
    }
  };

  return (
    <div className="mx-auto max-w-4xl p-6">
      <Link to="/pricing" className="text-sm text-slate-600 hover:underline">← Pricing hub</Link>
      <h1 className="mt-4 text-2xl font-semibold">AI Pricing Recommendations</h1>
      <div className="mt-4 flex gap-2">
        <input value={productId} onChange={(e) => setProductId(e.target.value)} placeholder="Product ID" className="rounded-lg border px-3 py-2 text-sm" />
        <button type="button" onClick={() => void run()} className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white">Get recommendation</button>
      </div>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      {rec && (
        <div className="mt-6 rounded-xl border bg-white p-6">
          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            <div><dt className="text-slate-500">Current</dt><dd className="font-semibold">{rec.current_price}</dd></div>
            <div><dt className="text-slate-500">Recommended</dt><dd className="font-semibold text-emerald-700">{rec.recommended}</dd></div>
            <div><dt className="text-slate-500">Confidence</dt><dd>{(rec.confidence * 100).toFixed(0)}%</dd></div>
            <div><dt className="text-slate-500">Margin</dt><dd>{rec.margin_percent?.toFixed(1) ?? '—'}%</dd></div>
          </dl>
          {rec.reason && <p className="mt-4 text-sm text-slate-600">{rec.reason}</p>}
        </div>
      )}
    </div>
  );
}

export function CompetitorPricingPage() {
  return (
    <div className="mx-auto max-w-4xl p-6">
      <Link to="/pricing" className="text-sm text-slate-600 hover:underline">← Pricing hub</Link>
      <h1 className="mt-4 text-2xl font-semibold">Competitor Pricing</h1>
      <p className="mt-2 text-sm text-slate-600">Competitor price data feeds AI recommendations. Use AI Pricing Recommendations to compare against captured competitor prices.</p>
      <Link to="/pricing/recommendations" className="mt-4 inline-block text-sm text-slate-700 hover:underline">View AI recommendations →</Link>
    </div>
  );
}
